/**
 * The TypeScript ad-push classifier — the second statement of FR-3's rule, the twin of PHP's
 * `App\Services\RankTracker\ClassifyAdPush`.
 *
 * The rule is stated once in prose (`components/rank-tracker.md § The ad-push rule (stated once)`)
 * and twice in code, one per runtime, because this repository has no codegen to derive one from the
 * other (`decisions/the-ad-push-suggestion-is-computed-never-stored-and-classified-once-per-runtime.md`,
 * NFR-2). Nothing mechanical keeps the two in step, so the first-match order and the reason-string
 * templates are pinned character-for-character in both — the byte-identical reason strings on screen
 * and in the CSV are the actual contract (FR-4 rounds `avg` server-side precisely so the two agree).
 *
 * This module is pure and render-free: it turns two already-computed facts plus three cutoffs into a
 * label and its reason string. It does **not** fetch anything and does **not** re-round — the server
 * already rounds the 7-day average organic rank to one decimal (FR-4) and this receives it as a
 * `number`. The cutoffs it compares are JSON `number`s (the backend emits numbers, not strings —
 * CORE-001/FEATURE-002); they are compared numerically, never coerced.
 *
 * The prototype's `localStorage`-backed per-brand cutoff store (`setBrandCutoffs` / `useBrandCutoffs`)
 * does **not** port — the cutoffs are a server entity now
 * (`design-input/rank-tracker-ad-push/sources.md § Scaffolding that must not ship`). `AdPushCutoffs`
 * is defined here for the classifier's own use and is deliberately independent of the wire types in
 * `src/types/ad-push.ts`; the column and the rules popover map the API response onto it.
 */
import type { ComponentType, ReactElement } from "react";
import { ArrowDownRight, ArrowUpRight, Equal } from "lucide-react";

/** The horizontal dashed line used for Long shot — no lucide equivalent exists. */
function DashedLine({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path d="M3 12h3M10.5 12h3M18 12h3" />
    </svg>
  );
}

/** The four ad-push labels, checked in this order (first match wins). */
export type AdPushLabel = "ease-off" | "hold" | "push" | "long-shot";

/** The three per-brand cutoffs the rule compares against. JSON numbers, compared numerically. */
export interface AdPushCutoffs {
  easeOff: number;
  hold: number;
  push: number;
}

/** The single TypeScript home of the cutoff defaults (3.5 / 10 / 30). */
export const DEFAULT_CUTOFFS: AdPushCutoffs = { easeOff: 3.5, hold: 10, push: 30 };

/** Per-label presentation metadata — verbatim from the design of record (`src/lib/adPush.ts`). */
export const AD_PUSH_META: Record<
  AdPushLabel,
  { name: string; badgeClass: string; meaning: string; icon: ComponentType<{ className?: string }> }
> = {
  "ease-off": {
    name: "Ease off",
    badgeClass: "bg-status-error-soft text-foreground",
    icon: ArrowDownRight,
    meaning:
      "Already ranks near the top organically while you're paying for an ad. The ad may be buying clicks you'd get anyway, so consider lowering the bid. Own brand terms may still need ad coverage.",
  },
  hold: {
    name: "Hold",
    badgeClass: "bg-status-success-soft text-foreground",
    icon: Equal,
    meaning: "Ranks well organically. Keep current spend and watch for slipping.",
  },
  push: {
    name: "Push",
    badgeClass: "bg-status-success-strong text-primary-foreground hover:bg-status-success-strong",
    icon: ArrowUpRight,
    meaning: "Visible but not winning. This is where ads are most likely to help lift organic rank.",
  },
  "long-shot": {
    name: "Long shot",
    badgeClass: "bg-status-warning-soft text-foreground",
    icon: DashedLine,
    meaning:
      "Ranks far down organically. Ads would carry it alone, so spend only if the keyword matters for other reasons.",
  },
};

/** The column header tooltip, verbatim from the design of record. */
export const AD_PUSH_DEFINITION =
  "A suggestion for each keyword: ease off, hold, push or treat as a long shot with ad spend. It's based on the keyword's 7-day average organic rank and whether an ad is running. Edit the rules with the sliders icon.";

/** What `classifyAdPush` returns: the label and its already-built reason string. */
export interface AdPushResult {
  label: AdPushLabel;
  reason: string;
}

/**
 * Classifies a keyword from its server-computed 7-day facts and the brand's cutoffs.
 *
 * @param avg       the mean of the keyword's non-null organic ranks over the latest 7 days of data,
 *                  **already rounded to one decimal server-side** (FR-4); `null` when no day in that
 *                  window carried an organic rank. Not re-rounded here.
 * @param adRunning any non-null sponsored rank in that same 7-day window (FR-4). Gates only Ease off.
 * @param cutoffs   the brand's cutoffs (numbers; defaults `DEFAULT_CUTOFFS`), compared numerically.
 *
 * First-match-wins (`components/rank-tracker.md § The ad-push rule (stated once)`):
 * Ease off → Hold → Push → Long shot, with `avg === null` falling through to Long shot.
 */
export function classifyAdPush(
  avg: number | null,
  adRunning: boolean,
  cutoffs: AdPushCutoffs,
): AdPushResult {
  const reason = (name: string, cutoff: number | null): string =>
    avg === null
      ? `${name}: no organic rank in the last 7 days`
      : cutoff === null
        ? `${name}: 7-day organic avg ${avg}, worse than Push cutoff ${cutoffs.push}`
        : `${name}: 7-day organic avg ${avg}, cutoff ${cutoff}`;

  if (avg !== null && avg <= cutoffs.easeOff && adRunning) {
    return { label: "ease-off", reason: reason("Ease off", cutoffs.easeOff) + " with an ad running" };
  }
  if (avg !== null && avg <= cutoffs.hold) {
    return { label: "hold", reason: reason("Hold", cutoffs.hold) };
  }
  if (avg !== null && avg <= cutoffs.push) {
    return { label: "push", reason: reason("Push", cutoffs.push) };
  }
  return { label: "long-shot", reason: reason("Long shot", null) };
}

/** True when the cutoffs are weakly ascending — `easeOff <= hold <= push`. */
export function cutoffsInOrder(cutoffs: AdPushCutoffs): boolean {
  return cutoffs.easeOff <= cutoffs.hold && cutoffs.hold <= cutoffs.push;
}

/** True when any of the three cutoffs differs from its default (value-based, matching the prototype). */
export function isCustom(cutoffs: AdPushCutoffs): boolean {
  return (
    cutoffs.easeOff !== DEFAULT_CUTOFFS.easeOff ||
    cutoffs.hold !== DEFAULT_CUTOFFS.hold ||
    cutoffs.push !== DEFAULT_CUTOFFS.push
  );
}
